import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, BarChart3 } from "lucide-react";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  Legend,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from "recharts";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { companyOrSharedDemo } from "@/lib/demo";

const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const UNASSIGNED = "Unassigned";
const PROJECTED = "Projected";
const WEEKDAYS = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];

/** Mowing season: March 15 → October 31. */
function inSeason(isoDate: string) {
  const year = isoDate.slice(0, 4);
  return isoDate >= `${year}-03-15` && isoDate <= `${year}-10-31`;
}

type DueCustomer = {
  frequency: string | null;
  service_day: string | null;
  est_duration_minutes: number | null;
};

/** Whether a customer's recurring service falls on the given ISO date. */
function isDueOn(c: DueCustomer, isoDate: string) {
  if (!c.service_day) return false;
  if (!inSeason(isoDate)) return false;
  const d = new Date(`${isoDate}T00:00:00`);
  const weekday = WEEKDAYS[d.getDay()];
  const target = c.service_day.trim().toLowerCase();
  const matchesDay = weekday === target || weekday.startsWith(target.slice(0, 3));
  if (!matchesDay) return false;

  const freq = (c.frequency ?? "weekly").trim().toLowerCase();
  if (freq.includes("one") || freq.includes("once") || freq.includes("single")) return false;
  if (freq.includes("bi") || freq.includes("every other") || freq.includes("2 week")) {
    const weekIndex = Math.floor(d.getTime() / (7 * 24 * 60 * 60 * 1000));
    return weekIndex % 2 === 0;
  }
  if (freq.includes("month")) return d.getDate() <= 7;
  return true;
}


const CREW_COLORS = [
  "hsl(var(--primary))",
  "hsl(var(--primary-glow))",
  "hsl(140 45% 55%)",
  "hsl(155 40% 25%)",
  "hsl(100 40% 50%)",
  "hsl(170 35% 40%)",
];

function startOfWeek(d: Date) {
  const date = new Date(d);
  const day = (date.getDay() + 6) % 7; // Monday = 0
  date.setDate(date.getDate() - day);
  date.setHours(0, 0, 0, 0);
  return date;
}

function toISO(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")}`;
}

function fmtHours(minutes: number) {
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  if (!h) return `${m}m`;
  return m ? `${h}h ${m}m` : `${h}h`;
}

type JobRow = {
  scheduled_date: string;
  status: string;
  crew: string | null;
  est_duration_minutes: number | null;
  actual_duration_minutes: number | null;
};

type AvailRow = {
  day_of_week: number;
  is_available: boolean;
  target_hours: number | null;
};

type RouteRow = {
  route_date: string;
  crew_name: string | null;
  total_duration_minutes: number | null;
};

export default function WorkloadAnalysis({
  companyId,
  embedded = false,
}: {
  companyId: string;
  embedded?: boolean;
}) {
  const [open, setOpen] = useState(embedded);

  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  const [jobs, setJobs] = useState<JobRow[]>([]);
  const [routes, setRoutes] = useState<RouteRow[]>([]);
  const [avail, setAvail] = useState<AvailRow[]>([]);
  const [customers, setCustomers] = useState<DueCustomer[]>([]);
  const [loading, setLoading] = useState(false);

  const weekEnd = useMemo(() => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + 6);
    return d;
  }, [weekStart]);

  useEffect(() => {
    if (!open || !companyId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      const [{ data: jobData }, { data: routeData }, { data: availData }, { data: custData }] =
        await Promise.all([
          supabase
            .from("jobs")
            .select("scheduled_date, status, crew, est_duration_minutes, actual_duration_minutes")
            .eq("company_id", companyId)
            .gte("scheduled_date", toISO(weekStart))
            .lte("scheduled_date", toISO(weekEnd)),
          supabase
            .from("routes")
            .select("route_date, crew_name, total_duration_minutes")
            .eq("company_id", companyId)
            .gte("route_date", toISO(weekStart))
            .lte("route_date", toISO(weekEnd)),
          supabase
            .from("company_availability")
            .select("day_of_week, is_available, target_hours")
            .eq("company_id", companyId),
          supabase
            .from("customers")
            .select("frequency, service_day, est_duration_minutes")
            .or(companyOrSharedDemo(companyId)),
        ]);
      if (!cancelled) {
        setJobs((jobData as JobRow[]) ?? []);
        setRoutes((routeData as RouteRow[]) ?? []);
        setAvail((availData as AvailRow[]) ?? []);
        setCustomers((custData as DueCustomer[]) ?? []);
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, companyId, weekStart, weekEnd]);

  const todayISO = toISO(new Date());

  const weekDays = useMemo(
    () =>
      DAY_LABELS.map((label, i) => {
        const d = new Date(weekStart);
        d.setDate(d.getDate() + i);
        return { label, date: d, iso: toISO(d) };
      }),
    [weekStart]
  );

  /** Days with no saved jobs yet get a projection from recurring customers. */
  const projections = useMemo(() => {
    const map: Record<string, { stops: number; minutes: number }> = {};
    weekDays.forEach(({ iso }) => {
      const hasJobs = jobs.some((j) => j.scheduled_date === iso);
      if (hasJobs || iso < todayISO) return;
      const due = customers.filter((c) => isDueOn(c, iso));
      if (!due.length) return;
      map[iso] = {
        stops: due.length,
        minutes: due.reduce((s, c) => s + (c.est_duration_minutes ?? 45), 0),
      };
    });
    return map;
  }, [customers, jobs, weekDays, todayISO]);

  const crews = useMemo(() => {
    const set = new Set<string>();
    jobs.forEach((j) => set.add(j.crew?.trim() || UNASSIGNED));
    routes.forEach((r) => {
      const n = r.crew_name?.trim();
      if (n && n !== "All crews") set.add(n);
    });
    return Array.from(set).sort((a, b) =>
      a === UNASSIGNED ? 1 : b === UNASSIGNED ? -1 : a.localeCompare(b)
    );
  }, [jobs, routes]);

  const hasProjection = Object.keys(projections).length > 0;
  const series = useMemo(
    () => (hasProjection ? [...crews, PROJECTED] : crews),
    [crews, hasProjection]
  );

  const chartData = useMemo(() => {
    return weekDays.map(({ label, date: d, iso }) => {
      const isPast = iso < todayISO;
      const row: Record<string, string | number> = { day: label, date: iso };
      let stops = 0;
      let total = 0;
      let drive = 0;
      crews.forEach((crew) => {
        const dayJobs = jobs.filter(
          (j) => j.scheduled_date === iso && (j.crew?.trim() || UNASSIGNED) === crew
        );
        const work = dayJobs.reduce(
          (sum, j) => sum + (j.actual_duration_minutes ?? j.est_duration_minutes ?? 45),
          0
        );
        const driveMin =
          isPast && crew !== UNASSIGNED
            ? routes
                .filter((r) => r.route_date === iso && r.crew_name?.trim() === crew)
                .reduce((sum, r) => sum + Number(r.total_duration_minutes ?? 0), 0)
            : 0;
        const minutes = work + driveMin;
        row[crew] = Number((minutes / 60).toFixed(2));
        row[`${crew}__min`] = minutes;
        row[`${crew}__work`] = work;
        row[`${crew}__stops`] = dayJobs.length;
        row[`${crew}__drive`] = driveMin;

        stops += dayJobs.length;
        total += minutes;
        drive += driveMin;
      });

      if (hasProjection) {
        const p = projections[iso];
        row[PROJECTED] = p ? Number((p.minutes / 60).toFixed(2)) : 0;
        row[`${PROJECTED}__min`] = p?.minutes ?? 0;
        row[`${PROJECTED}__stops`] = p?.stops ?? 0;
        stops += p?.stops ?? 0;
        total += p?.minutes ?? 0;
      }

      const a = avail.find((x) => x.day_of_week === d.getDay());
      row.target = a && a.is_available ? Number(a.target_hours ?? 8) : null;
      row.stops = stops;
      row.minutes = total;
      row.drive = drive;
      return row;
    });
  }, [jobs, routes, avail, crews, weekDays, todayISO, projections, hasProjection]);

  const totalMinutes = chartData.reduce((s, d) => s + Number(d.minutes ?? 0), 0);
  const totalStops = chartData.reduce((s, d) => s + Number(d.stops ?? 0), 0);
  const totalDrive = chartData.reduce((s, d) => s + Number(d.drive ?? 0), 0);

  const chartConfig = useMemo(() => {
    const cfg: Record<string, { label: string }> = {};
    series.forEach((s) => {
      cfg[s] = { label: s === PROJECTED ? "Projected (not yet scheduled)" : s };
    });
    cfg.target = { label: "Target hours" };
    return cfg;
  }, [series]);



  const shiftWeek = (delta: number) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + delta * 7);
    setWeekStart(d);
  };

  const rangeLabel = `${weekStart.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  })} – ${weekEnd.toLocaleDateString(undefined, { month: "short", day: "numeric" })}`;

  const body = (
    <>


        <div className="flex items-center justify-between gap-2">
          <Button variant="ghost" size="icon" onClick={() => shiftWeek(-1)} aria-label="Previous week">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div className="text-center">
            <p className="text-sm font-semibold">{rangeLabel}</p>
            <p className="text-xs text-muted-foreground">
              {totalStops} stops · {fmtHours(totalMinutes)} total
              {totalDrive > 0 && <> · {fmtHours(totalDrive)} driving</>}
            </p>
          </div>
          <Button variant="ghost" size="icon" onClick={() => shiftWeek(1)} aria-label="Next week">
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        <ChartContainer config={chartConfig} className="h-[300px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid vertical={false} strokeDasharray="3 3" />
              <XAxis dataKey="day" tickLine={false} axisLine={false} />
              <YAxis tickLine={false} axisLine={false} width={36} tickFormatter={(v) => `${v}h`} />
              <ChartTooltip
                content={
                  <ChartTooltipContent
                    formatter={(value, name, item) => {
                      const p = item?.payload ?? {};
                      const key = String(name);
                      if (key === "target" || key === "Target hours") {
                        return <span>Target: {fmtHours(Number(value) * 60)}</span>;
                      }
                      return (
                        <span>
                          {key}: {fmtHours(Number(p[`${key}__min`] ?? 0))} ·{" "}
                          {p[`${key}__stops`] ?? 0} stops
                        </span>
                      );
                    }}
                  />
                }
              />
              <Legend />
              {series.map((s, i) => (
                <Bar
                  key={s}
                  dataKey={s}
                  fill={s === PROJECTED ? "hsl(var(--muted-foreground))" : CREW_COLORS[i % CREW_COLORS.length]}
                  fillOpacity={s === PROJECTED ? 0.5 : 1}
                  radius={[4, 4, 0, 0]}
                />
              ))}


              <Line
                type="monotone"
                dataKey="target"
                name="Target hours"
                stroke="hsl(var(--destructive))"
                strokeWidth={2}
                strokeDasharray="6 4"
                dot={false}
                activeDot={false}
                connectNulls
                isAnimationActive={false}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </ChartContainer>

        <p className="text-xs text-muted-foreground">
          One bar per crew showing total day time — mow time plus actual drive time on past days.
          Future days without saved jobs show projected workload from recurring customers.
        </p>

        {loading && <p className="text-xs text-muted-foreground">Loading…</p>}
        {!loading && totalStops === 0 && (
          <p className="text-xs text-muted-foreground">No jobs scheduled this week.</p>
        )}
    </>
  );

  if (embedded) return <div className="space-y-4">{body}</div>;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <BarChart3 className="h-4 w-4" />
          Analysis
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Weekly workload</DialogTitle>
        </DialogHeader>
        {body}
      </DialogContent>
    </Dialog>

  );
}
